import React, { forwardRef, useMemo } from 'react';
import styled from 'styled-components';
import { ArrowUpRight } from 'lucide-react';
import { IMetricConfig, IWorkspacePerformance, METRICS, PerformanceMetric } from './performanceMetrics';
import PerformanceChart, { IPerformanceChartHandle } from './PerformanceChart';
import { campaignMobile } from '../../../utils/campaignBreakpoints';

interface IWorkspacePerformanceProps {
  data: IWorkspacePerformance | null;
  /** Cards to show, in order. Defaults to the eight workspace-wide stats. */
  metrics?: IMetricConfig[];
  loading: boolean;
  hidden: Set<PerformanceMetric>;
  onToggleMetric: (metric: PerformanceMetric) => void;
  /** Shows each card's leads on the Leads tab. Only a single campaign's page has one. */
  onOpenLeads?: (metric: PerformanceMetric) => void;
}

const WorkspacePerformance = forwardRef<IPerformanceChartHandle, IWorkspacePerformanceProps>(({ data, metrics = METRICS, loading, hidden, onToggleMetric, onOpenLeads }, chartRef) => {
  const enabled: IMetricConfig[] = useMemo(() => metrics.filter(m => !hidden.has(m.key)), [metrics, hidden]);
  // Rows of four; a shorter last row stretches its cards across, as in the design.
  const rows = useMemo(() => {
    const out: IMetricConfig[][] = [];
    for (let i = 0; i < metrics.length; i += 4) out.push(metrics.slice(i, i + 4));
    return out;
  }, [metrics]);

  return (
    <>
      <CardsGrid>
        {rows.map(row => (
        <CardsRow key={row[0].key}>
        {row.map(m => {
          const off = hidden.has(m.key);
          return (
            <CardCell key={m.key}>
            <StatCard
              type="button"
              $off={off}
              $withLink={!!onOpenLeads}
              aria-pressed={!off}
              title={off ? 'Show on chart' : 'Hide from chart'}
              onClick={() => onToggleMetric(m.key)}
            >
              <StatLabel>{m.label}</StatLabel>
              <StatValueRow>
                <StatIcon style={{ background: m.iconBg, borderColor: m.iconBorder, color: m.color }}>
                  <m.Icon size={20} strokeWidth={1.5} />
                </StatIcon>
                <StatValue>{loading && !data ? '–' : (data?.totals[m.key] ?? 0)}</StatValue>
              </StatValueRow>
            </StatCard>
            {/* Beside the card, not inside it — the card is itself a button (chart toggle).
                Only when there is something to see: a zero would open an empty list. */}
            {onOpenLeads && !!data?.totals[m.key] && (
              <LeadsLink type="button" title="View these leads" aria-label={`View leads: ${m.label}`} onClick={() => onOpenLeads(m.key)}>
                <ArrowUpRight size={16} strokeWidth={1.75} />
              </LeadsLink>
            )}
            </CardCell>
          );
        })}
        </CardsRow>
        ))}
      </CardsGrid>

      <ChartSection>
        {data && data.series.length > 0 ? (
          <PerformanceChart ref={chartRef} series={data.series} granularity={data.granularity} metrics={enabled} />
        ) : (
          <ChartEmpty>{loading ? null : 'No activity in this period.'}</ChartEmpty>
        )}
      </ChartSection>
    </>
  );
});

WorkspacePerformance.displayName = 'WorkspacePerformance';

export default WorkspacePerformance;

const CardsGrid = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  padding: 0 24px;
  ${campaignMobile`gap: 10px; padding: 0 16px;`}
`;

const CardsRow = styled.div`
  display: flex; gap: 16px;
  & > * { flex: 1 1 0; }
  ${campaignMobile`flex-wrap: wrap; gap: 10px; & > * { flex: 1 1 calc(50% - 5px); }`}
`;

const CardCell = styled.div`position: relative; display: flex; min-width: 0;`;

const StatCard = styled.button<{ $off: boolean; $withLink?: boolean }>`
  flex: 1; display: flex; flex-direction: column; align-items: flex-start; gap: 12px;
  padding: 16px; min-width: 0;
  ${({ $withLink }) => $withLink && 'padding-right: 44px;'}
  background: #FFFFFF;
  border: 1px solid #E5E7EB; border-radius: 12px;
  text-align: left; cursor: pointer;
  opacity: ${({ $off }) => ($off ? 0.45 : 1)};
  filter: ${({ $off }) => ($off ? 'grayscale(1)' : 'none')};
  transition: opacity 0.15s ease, filter 0.15s ease, box-shadow 0.15s ease;
  &:hover { box-shadow: 0 2px 10px rgba(16, 24, 40, 0.06); }
`;

const LeadsLink = styled.button`
  position: absolute; top: 12px; right: 12px;
  width: 24px; height: 24px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid #E5E7EB; border-radius: 6px; background: #FFFFFF;
  color: #6B7280; cursor: pointer;
  &:hover { color: #454DE5; border-color: #C7CBFF; background: #F5F6FF; }
`;

const StatLabel = styled.span`
  max-width: 100%;
  font-family: 'Nunito Sans', NunitoSans, sans-serif; font-size: 14px; font-weight: 600; line-height: 20px;
  color: #252B37;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const StatValueRow = styled.div`
  display: flex; align-items: center; gap: 8px;
`;

const StatIcon = styled.span`
  width: 30px; height: 30px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid; border-radius: 8px;
`;

const StatValue = styled.span`
  font-family: 'InterVariable', 'Inter', NunitoSans, sans-serif; font-size: 24px; font-weight: 500; line-height: 24px;
  color: #252B37;
`;

const ChartSection = styled.div`
  padding: 23px 24px;
  ${campaignMobile`padding: 16px;`}
`;

const ChartEmpty = styled.div`
  height: 330px;
  display: flex; align-items: center; justify-content: center;
  font-family: NunitoSans; font-size: 14px; color: #8C96AD;
`;
